import * as React from 'react'
import { closeArticleCollectionModal } from '../../store/modals/actions'
import ModalHeader from './Parts/ModalHeader'
import { AppState } from '../../store'
import { AnyAction, Dispatch } from 'redux'
import { connect } from 'react-redux'
import ModalWindow from './Parts/ModalWindow'
import ModalMiddle from './Parts/ModalMiddle'
import ModalContent from './Parts/ModalContent'
import { useTranslation } from 'react-i18next'
import ModalLoader from './Parts/ModalLoader'
import Button from '../Button/Button'
import { ArticleCollectionsController } from '../../controllers'
import { CurrentUser, DisplayableError, ArticleCollection, Locale, PublicLocale } from '../../types'
import TooltipError from '../Tooltips/ErrorTooltip'
import MobilityHelper from '../../helpers/MobilityHelper'

interface IStateToProps {
  currentUser: CurrentUser
  helpCenterId: string
  locale?: PublicLocale
  articleCollection?: ArticleCollection
  onSubmit: (articleCollection: ArticleCollection) => void
}

interface IDispatchToProps {
  close: typeof closeArticleCollectionModal
}

type IProps = IDispatchToProps & IStateToProps

interface IState {
  didInitialLoad: boolean
  locale: Locale
  articleCollection: ArticleCollection
  errors: DisplayableError[]
}

const ArticleCollectionModal = (props: IProps) => {
  const { t } = useTranslation()
  const { currentUser, locale } = props
  const [state, setState] = React.useState<IState>({
    didInitialLoad: false,
    locale: null,
    articleCollection: null,
    errors: [],
  })
  const { helpCenterId } = props
  const { articleCollection, errors, didInitialLoad } = state

  React.useEffect(() => {
    getForm().catch(console.error)
  }, [])

  const getForm = async () => {
    try {
      const { article_collection, default_locale } = await ArticleCollectionsController.getForm(helpCenterId, { id: props.articleCollection ? props.articleCollection.id : null })

      setState({
        ...state,
        articleCollection: {
          ...article_collection,
          ...props.articleCollection,
        },
        didInitialLoad: true,
      })
    } catch (ex) {
      console.error(ex)
    }
  }


  const onTitleChange = (e) => {
    const updatedTranslations = MobilityHelper.updateTranslation(locale, articleCollection.translations, 'title', e.currentTarget.value)
    setState({
      ...state,
      articleCollection: {
        ...articleCollection,
        translations: updatedTranslations
      }
    })
  }

  const onDescriptionChange = (e) => {
    const updatedTranslations = MobilityHelper.updateTranslation(locale, articleCollection.translations, 'description', e.currentTarget.value)
    setState({
      ...state,
      articleCollection: {
        ...articleCollection,
        translations: updatedTranslations
      }
    })
  }

  const onCloseClick = () => {
    props.close()
  }

  const onFormSubmit = async (e) => {
    e.preventDefault()

    try {
      let response = null
      if (articleCollection.id) {
        response = await ArticleCollectionsController.update(helpCenterId, articleCollection)
      } else {
        response = await ArticleCollectionsController.create(helpCenterId, articleCollection)
      }

      if (response.errors) {
        setState({ ...state, errors: response.errors })
      } else {
        if (props.onSubmit) props.onSubmit(response)
        props.close()
      }

    } catch (ex) {
      console.error(ex)
    }
  }

  const onErrorsDismiss = () => {
    setState({ ...state, errors: [] })
  }

  return (
    <ModalWindow>
      <ModalHeader
        title={articleCollection?.id ? t('ArticleCollectionModal::Update collection') : t('ArticleCollectionModal::Create collection')}
        onCloseClick={onCloseClick}
      />

      {!didInitialLoad && <ModalLoader />}
      {didInitialLoad && <ModalMiddle>
        <ModalContent>
          <form onSubmit={onFormSubmit}>
            <div className='grid'>
              <div className='grid-cell with-12col'>
                <div className='form-item'>
                  <label>{t('ArticleCollectionModal::Title')} <span>*</span></label>
                  <input
                    type='text'
                    placeholder={t('ArticleCollectionModal::Title')}
                    onChange={onTitleChange}
                    value={articleCollection?.translations[locale]?.title}
                  />
                </div>
              </div>

              <div className='grid-cell with-12col'>
                <div className='form-item'>
                  <label>{t('ArticleCollectionModal::Description')} <span>*</span></label>
                  <input
                    type='text'
                    placeholder={t('ArticleCollectionModal::Description')}
                    onChange={onDescriptionChange}
                    value={articleCollection?.translations[locale]?.description}
                  />
                </div>
              </div>
            </div>
            <input type='submit' style={{ display: 'none' }} onClick={onFormSubmit} />
          </form>
        </ModalContent>

        <div className='modal-footer'>
          <div />
          <div key='main-action' className='popover-wrapper'>
            <TooltipError
              errors={errors}
              onDismiss={onErrorsDismiss}
            />
            <Button
              type='success'
              text={articleCollection?.id ? t('ArticleCollectionModal::Update') : t('ArticleCollectionModal::Create')}
              onClick={onFormSubmit}
            />
          </div>
        </div>
      </ModalMiddle>}
    </ModalWindow >
  )
}

const mapStateToProps = (state: AppState): IStateToProps => {
  const {
    authentication: {
      currentUser
    },
    modals: {
      articleCollectionModal: {
        locale,
        helpCenterId,
        articleCollection,
        onSubmit,
      }
    }
  } = state

  return {
    currentUser: currentUser,
    locale: locale,
    helpCenterId: helpCenterId,
    articleCollection: articleCollection,
    onSubmit: onSubmit,
  }
}

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>): IDispatchToProps => {
  return {
    close: () => dispatch(closeArticleCollectionModal()),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(ArticleCollectionModal)